import React, { useEffect, useMemo, useRef, useState } from "react";
import ButtonBase from "@mui/material/ButtonBase";
import Popover from "@mui/material/Popover";
import TextField from "@mui/material/TextField";
import clsx from "clsx";
import { UserOption } from "utils/models/user";

interface DropdownSelectProps {
  value: number;
  onChange: (value: number) => void;
  options: UserOption[];
  addStyle?: string;
}

// Max rows ever rendered in the list
const MAX_RESULTS = 3000;
const SEARCH_DEBOUNCE_MS = 120;

type IndexedUser = UserOption & { _search: string };

const DropdownSelectUsers: React.FC<DropdownSelectProps> = ({
  value,
  onChange,
  options,
  addStyle,
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [highlight, setHighlight] = useState(0);
  const listRef = useRef<HTMLDivElement>(null);
  const open = Boolean(anchorEl);


  const indexed = useMemo<IndexedUser[]>(
    () =>
      options.map((userOption) => ({
        ...userOption,
        _search: `${userOption.fullName ?? ""} ${userOption.email ?? ""}`.toLowerCase(),
      })),
    [options]
  );

  const selected = useMemo(
    () => (value ? indexed.find((userOption) => userOption.id === value) ?? null : null),
    [indexed, value]
  );

  // Debouncing to prevent excessive re-filtering.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQuery(query), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [query]);

  // Display the first MAX_RESULTS matching user's search string.
  const { results, hasMore } = useMemo(() => {
    if (!open) return { results: [] as IndexedUser[], hasMore: false };
    const q = debouncedQuery.trim().toLowerCase();
    const out: IndexedUser[] = [];
    let more = false;
    for (const userOption of indexed) {
      if (!q || userOption._search.includes(q)) {
        if (out.length === MAX_RESULTS) {
          more = true;
          break;
        }
        out.push(userOption);
      }
    }
    return { results: out, hasMore: more };
  }, [indexed, debouncedQuery, open]);

  useEffect(() => {
    setHighlight(0);
  }, [results]);

  // Keep the keyboard-highlighted row visible
  useEffect(() => {
    const el = listRef.current?.children[highlight] as HTMLElement | undefined;
    el?.scrollIntoView({ block: "nearest" });
  }, [highlight]);

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    setQuery("");
    setDebouncedQuery("");
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => setAnchorEl(null);

  const handleSelect = (user: IndexedUser) => {
    onChange(user.id);
    handleClose();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlight((h) => Math.min(h + 1, results.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlight((h) => Math.max(h - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      const user = results[highlight];
      if (user) handleSelect(user);
    }
  };

  return (
    <>
      <ButtonBase
        onClick={handleOpen}
        aria-haspopup="listbox"
        aria-expanded={open}
        className={clsx(
          "dropdown-select",
          "!font-inter",
          "!w-[208px]",
          "!h-[52px]",
          "border-l",
          "border-transparent",
          "!font-semibold",
          "!text-[14px]",
          "!leading-[18px]",
          addStyle
        )}
        sx={{
          justifyContent: "space-between",
          gap: "8px",
          padding: "0 8px 0 14px",
          color: "#0093C8",
          textAlign: "left",
        }}
      >
        <span
          style={{
            minWidth: 0,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {selected?.fullName ?? ""}
        </span>
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="currentColor"
          aria-hidden="true"
          style={{
            flexShrink: 0,
            opacity: 0.54,
            transform: open ? "rotate(180deg)" : "none",
          }}
        >
          <path d="M7 10l5 5 5-5z" />
        </svg>
      </ButtonBase>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        transitionDuration={0}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        PaperProps={{ sx: { width: 300, maxWidth: "90vw" } }}
      >
        <div style={{ padding: 8, borderBottom: "1px solid #E5E7EB" }}>
          <TextField
            autoFocus
            fullWidth
            size="small"
            placeholder="Search users..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            inputProps={{
              autoComplete: "off",
              style: { fontFamily: "Inter, sans-serif", fontSize: 14 },
            }}
          />
        </div>

        <div
          ref={listRef}
          role="listbox"
          style={{ maxHeight: 320, overflowY: "auto" }}
        >
          {results.map((user, i) => (
            <div
              key={user.id}
              role="option"
              aria-selected={user.id === value}
              onClick={() => handleSelect(user)}
              onMouseEnter={() => setHighlight(i)}
              style={{
                padding: "6px 16px",
                cursor: "pointer",
                fontFamily: "Inter, sans-serif",
                backgroundColor: i === highlight ? "#F4F8F9" : "transparent",
                fontWeight: user.id === value ? 600 : 400,
              }}
            >
              <div
                style={{
                  fontSize: 14,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {user.fullName}
              </div>
              {user.email && user.email !== user.fullName && (
                <div
                  style={{
                    fontSize: 12,
                    color: "#6B7280",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {user.email}
                </div>
              )}
            </div>
          ))}
          {results.length === 0 && (
            <div
              style={{
                padding: "12px 16px",
                fontSize: 14,
                color: "#6B7280",
                fontFamily: "Inter, sans-serif",
              }}
            >
              No matching users
            </div>
          )}
        </div>

        {hasMore && (
          <div
            style={{
              padding: "6px 16px",
              fontSize: 12,
              color: "#6B7280",
              borderTop: "1px solid #E5E7EB",
              fontFamily: "Inter, sans-serif",
            }}
          >
            Showing the first {MAX_RESULTS}.<br/>Keep typing to narrow the list.
          </div>
        )}
      </Popover>
    </>
  );
};

export default React.memo(DropdownSelectUsers);